import type { ReactNode } from 'react';
import { CipherGrid } from '../routes/-login/CipherGrid';
import { useSession } from '../session';
import { Brand } from './Brand';
import styles from './EntryLayout.module.css';

type Props = {
  title: ReactNode;
  description?: ReactNode;
  /** 1-based position in a multi-step flow. */
  step?: { current: number; total: number };
  /** Shown above the title, e.g. a back link inside a sub-view. */
  eyebrow?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Brand panel beside the form on wide screens. */
  visual?: boolean;
};

/**
 * Branded full-page surface for everything that happens before the app shell:
 * sign-in, first-run setup, unlocking a new device, and approving a device.
 */
export function EntryLayout({
  title,
  description,
  step,
  eyebrow,
  children,
  footer,
  visual = true,
}: Props) {
  const { user, logout } = useSession();

  return (
    <main className={`${styles.page} ${visual ? '' : styles.pageSolo}`}>
      <div className={styles.column}>
        <header className={styles.top}>
          <Brand size="hero" />
          {user ? (
            <div className={styles.account}>
              <span className={styles.accountEmail} title={user.email}>
                {user.email}
              </span>
              <button type="button" className={styles.signOut} onClick={() => void logout()}>
                Sign out
              </button>
            </div>
          ) : null}
        </header>
        <div className={styles.body}>
          <div className={styles.inner}>
            {step ? (
              <div className={styles.progress}>
                <div
                  className={styles.progressBars}
                  role="progressbar"
                  aria-valuemin={1}
                  aria-valuemax={step.total}
                  aria-valuenow={step.current}
                  aria-label={`Step ${step.current} of ${step.total}`}
                >
                  {Array.from({ length: step.total }, (_, index) => (
                    <span
                      key={index}
                      className={`${styles.progressBar} ${index < step.current ? styles.progressBarDone : ''}`}
                    />
                  ))}
                </div>
                <span className={styles.progressLabel}>
                  Step {step.current} of {step.total}
                </span>
              </div>
            ) : null}
            {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
            <div className={styles.heading}>
              <h1>{title}</h1>
              {description ? <p>{description}</p> : null}
            </div>
            {children}
          </div>
        </div>
        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
      {visual ? (
        <section className={styles.visual} aria-hidden="true">
          <CipherGrid />
        </section>
      ) : null}
    </main>
  );
}

/** Full-width primary/secondary action stack used at the bottom of entry forms. */
export function EntryActions({ children }: { children: ReactNode }) {
  return <div className={styles.actions}>{children}</div>;
}

export function EntryNotice({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'danger';
  children: ReactNode;
}) {
  return (
    <div
      className={`${styles.notice} ${tone === 'danger' ? styles.noticeDanger : ''}`}
      role={tone === 'danger' ? 'alert' : undefined}
    >
      {children}
    </div>
  );
}
